import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';
import { marketplaceSupabase } from '@/integrations/supabase/marketplaceClient';
import { generateWebPWithFallback } from '@/utils/imageUtils';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useTemplate } from '@/contexts/TemplateContext';

import { useBranding } from '@/contexts/BrandingContext';

export interface HeroBlockProps {
  variant?: 'classic' | 'profile' | 'modern-search';
  title?: string;
  description?: string;
  primaryButtonText?: string;
  primaryButtonAction?: string;
  secondaryButtonText?: string;
  secondaryButtonAction?: string;
  backgroundImageUrl?: string;
  socialProofText?: string;
  socialProofSubtext?: string;
  floatingCard1Text?: string;
  floatingCard2Text?: string;
  overlayOpacity?: number;
  textColor?: 'light' | 'dark';
  imagePosition?: string;
}

export const HeroBlock: React.FC<HeroBlockProps> = ({
  variant = 'classic',
  title = "Encontre o imóvel dos seus sonhos",
  description = "Explore nossa vasta seleção de casas e apartamentos em bairros premium. Simplificamos o processo de compra e aluguel para você.",
  primaryButtonText = "Ver Imóveis",
  primaryButtonAction = "#imoveis",
  secondaryButtonText = "Falar com Corretor",
  secondaryButtonAction = "#contato",
  backgroundImageUrl = "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
  socialProofText = "Mais de 1.000 clientes",
  socialProofSubtext = "satisfeitos na região",
  floatingCard1Text = "Imóveis Premium",
  floatingCard2Text = "Compra Segura",
  overlayOpacity = 30,
  textColor = 'light',
  imagePosition = 'center center',
}) => {
  const { tenant, tenantId } = useTenant();
  const { currentTemplate } = useTemplate();
  const { branding } = useBranding();
  const siteTemplate = currentTemplate;

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoadingProfile, setIsLoadingProfile] = useState(siteTemplate === 'profile');
  const [profileData, setProfileData] = useState<{
    name: string;
    avatarUrl?: string;
    creci?: string;
    role?: string;
    whatsapp?: string;
    email?: string;
  }>({ name: 'Corretor' });

  // Use branding defaults if the props match the hardcoded initial state
  const isDefaultTitle = title === "Encontre o imóvel dos seus sonhos";
  const displayTitle = (isDefaultTitle && branding?.hero_title) ? branding.hero_title : title;

  const isDefaultDesc = description === "Explore nossa vasta seleção de casas e apartamentos em bairros premium. Simplificamos o processo de compra e aluguel para você.";
  const displayDesc = (isDefaultDesc && branding?.hero_description) ? branding.hero_description : description;

  const isDefaultImage = backgroundImageUrl === "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";
  const displayImage = (isDefaultImage && branding?.hero_image_url) ? branding.hero_image_url : backgroundImageUrl;

  // Search Bar States
  const [searchLocation, setSearchLocation] = useState(searchParams.get('location') || '');
  const [searchType, setSearchType] = useState(searchParams.get('type') || 'all');
  const [searchStatus, setSearchStatus] = useState(searchParams.get('status') || 'all');
  const [searchPriceMax, setSearchPriceMax] = useState(searchParams.get('priceMax') || 'all');

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchLocation) params.set('location', searchLocation);
    if (searchType !== 'all') params.set('type', searchType);
    if (searchStatus !== 'all') params.set('status', searchStatus);
    if (searchPriceMax !== 'all') params.set('priceMax', searchPriceMax);
    
    if (location.pathname === '/properties') {
      setSearchParams(params);

      // Scroll to properties grid
      const element = document.getElementById("imoveis-grid");
      if (element) {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      navigate(`/properties?${params.toString()}`);
    }
  };

  useEffect(() => {
    if (siteTemplate !== 'profile' || !tenantId) {
      setIsLoadingProfile(false);
      return;
    }

    const loadProfile = async () => {
      try {
        setIsLoadingProfile(true);
        const { data: realtorData } = await marketplaceSupabase
          .from('realtors')
          .select('*')
          .eq('tenant_id', tenantId)
          .eq('is_active', true)
          .order('is_primary', { ascending: false })
          .limit(1)
          .maybeSingle();

        const { data: tenantData } = await marketplaceSupabase
          .from('tenants')
          .select('name, logo_url')
          .eq('id', tenantId)
          .maybeSingle();

        setProfileData({
          // We no longer fallback to 'title' because it might contain classic layout text
          name: realtorData?.name || tenantData?.name || 'Corretor',
          avatarUrl: realtorData?.photo_url || tenantData?.logo_url || undefined,
          creci: realtorData?.creci || '',
          role: realtorData?.role || 'Corretor de Imóveis',
          whatsapp: realtorData?.whatsapp || realtorData?.phone || '',
          email: realtorData?.email || '',
        });
      } catch (err) {
        console.error('[HeroBlock] Error loading profile data:', err);
      } finally {
        setIsLoadingProfile(false);
      }
    };
    loadProfile();
  }, [tenantId, siteTemplate]);

  const imageUrl = displayImage ? generateWebPWithFallback(displayImage, 82, 1920, 1080).webp : '';

  const handleWhatsApp = (e: React.MouseEvent) => {
    if (profileData.whatsapp) {
      e.preventDefault();
      const phone = profileData.whatsapp.replace(/\D/g, '');
      window.open(`https://wa.me/55${phone}`, '_blank');
    }
  };

  const handleEmail = (e: React.MouseEvent) => {
    if (profileData.email) {
      e.preventDefault();
      navigator.clipboard.writeText(profileData.email);
      alert('E-mail copiado para a área de transferência!');
    }
  };

  if (siteTemplate === 'profile') {
    return (
      <section className="w-full px-4 pt-8 pb-4 animate-in fade-in duration-1000 flex justify-center">
        <div className="w-full max-w-md md:max-w-3xl lg:max-w-4xl bg-gradient-to-b from-[#151515] to-[#111111] rounded-3xl p-8 md:p-10 flex flex-col md:flex-row items-center md:items-center text-center md:text-left shadow-2xl relative overflow-hidden border border-[#262626]">
          {/* Subtle background glow on desktop */}
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[var(--brand-primary)] opacity-[0.07] rounded-full blur-[80px] pointer-events-none -mr-32 -mt-32"></div>

          {/* Avatar */}
          <div className="w-28 h-28 sm:w-32 sm:h-32 md:w-44 md:h-44 lg:w-48 lg:h-48 rounded-3xl overflow-hidden mb-6 md:mb-0 md:mr-10 shrink-0 transition-transform duration-500 hover:scale-[1.03] shadow-2xl ring-1 ring-white/10">
            {isLoadingProfile ? (
              <div className="w-full h-full bg-[#2A2A2A] animate-pulse"></div>
            ) : (
              <img
                src={profileData.avatarUrl ? generateWebPWithFallback(profileData.avatarUrl, 85, 300, 300).webp : (imageUrl || "https://i.pravatar.cc/300")}
                alt={profileData.name}
                className="w-full h-full object-cover"
                style={{ objectPosition: imagePosition }}
                loading="eager"
                decoding="async"
                {...({ fetchpriority: 'high' } as any)}
                width="300"
                height="300"
              />
            )}
          </div>

          {/* Content block */}
          <div className="flex-1 flex flex-col items-center md:items-start min-w-0 w-full z-10">
            {/* Disponível Pill */}
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#2A2A2A] bg-[#1A1A1A] mb-5 shadow-sm">
              <div className="w-1.5 h-1.5 rounded-full bg-[#00C853] animate-pulse"></div>
              <span className="text-[10px] font-bold text-[#00C853] tracking-wider uppercase">
                Disponível para atendimento
              </span>
            </div>

            <div className="flex flex-wrap items-center justify-center md:justify-start gap-2.5 mb-1.5 w-full">
              {isLoadingProfile ? (
                <div className="h-10 w-48 bg-[#2A2A2A] rounded animate-pulse"></div>
              ) : (
                <>
                  <h1 className="text-3xl sm:text-4xl md:text-[2.5rem] font-bold text-white tracking-tight break-words leading-tight">
                    {profileData.name}
                  </h1>
                  <svg className="w-6 h-6 md:w-7 md:h-7 shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.99-3.818-3.99-.48 0-.94.1-1.348.27C14.825 2.515 13.512 1.5 12 1.5s-2.825 1.015-3.422 2.28c-.407-.17-.867-.27-1.348-.27-2.108 0-3.818 1.78-3.818 3.99 0 .495.084.965.238 1.4-1.273.65-2.148 2.02-2.148 3.6 0 1.58.875 2.95 2.148 3.6-.154.435-.238.905-.238 1.4 0 2.21 1.71 3.99 3.818 3.99.48 0 .94-.1 1.348-.27.597 1.265 1.91 2.28 3.422 2.28s2.825-1.015 3.422-2.28c.407.17.867.27 1.348.27 2.108 0 3.818-1.78 3.818-3.99 0-.495-.084-.965-.238-1.4 1.273-.65 2.148-2.02 2.148-3.6z" fill="#1D9BF0" />
                    <path d="M10 16.5l-4-4 1.41-1.41L10 13.67l6.59-6.59L18 8.5l-8 8z" fill="white" />
                  </svg>
                </>
              )}
            </div>

            {isLoadingProfile ? (
              <div className="h-6 w-32 bg-[#2A2A2A] rounded animate-pulse mb-1.5"></div>
            ) : (
              <p className="text-zinc-300 text-base md:text-lg font-medium mb-1.5">
                {profileData.role || 'Corretor de Imóveis'}
              </p>
            )}

            {isLoadingProfile ? (
              <div className="h-4 w-24 bg-[#2A2A2A] rounded animate-pulse mb-8 mt-2"></div>
            ) : (
              profileData.creci && (
                <p className="text-zinc-500 text-xs md:text-sm font-semibold uppercase tracking-widest mb-8">
                  CRECI: {profileData.creci}
                </p>
              )
            )}

            {/* Buttons */}
            <div className="w-full flex flex-col sm:flex-row justify-center md:justify-start gap-4">
              {primaryButtonText && (
                <a
                  href={profileData.whatsapp ? '#' : primaryButtonAction}
                  onClick={profileData.whatsapp ? handleWhatsApp : undefined}
                  className="w-full sm:w-auto bg-[#1A1A1A] hover:bg-[#222222] border border-[#333333] hover:border-[#444444] text-white font-medium py-3 px-8 rounded-2xl flex items-center justify-center gap-3 transition-all cursor-pointer shadow-md active:scale-[0.98]"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>
                  {primaryButtonText}
                </a>
              )}
              {secondaryButtonText && (
                <a
                  href={profileData.email ? '#' : secondaryButtonAction}
                  onClick={profileData.email ? handleEmail : undefined}
                  className="w-full sm:w-auto bg-[#1A1A1A] hover:bg-[#222222] border border-[#333333] hover:border-[#444444] text-white font-medium py-3 px-8 rounded-2xl flex items-center justify-center gap-3 transition-all cursor-pointer shadow-md active:scale-[0.98]"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2" /><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" /></svg>
                  {secondaryButtonText}
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full pt-2 sm:pt-3 pb-8 px-2 sm:px-4 md:px-6 lg:px-8 bg-background flex items-center justify-center animate-in fade-in duration-1000">
      <div className="relative w-full max-w-[1600px] 2xl:max-w-[1800px] h-auto min-h-[505px] py-7 md:py-0 md:h-[72vh] lg:h-[77vh] md:min-h-[570px] max-h-[800px] xl:max-h-[900px] rounded-3xl overflow-hidden flex items-center justify-center shadow-2xl border border-border/40 mx-auto">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={imageUrl || (backgroundImageUrl ? generateWebPWithFallback(backgroundImageUrl, 82, 1920, 1080).webp : '')}
            alt="Hero Background"
            className="w-full h-full object-cover transition-transform duration-[20s] hover:scale-105"
            style={{ objectPosition: imagePosition }}
            loading="eager"
            decoding="async"
            {...({ fetchpriority: 'high' } as any)}
          />
          {/* Overlay to ensure text readability if needed */}
          <div 
            className="absolute inset-0 transition-colors duration-300"
            style={{
              backgroundColor: `rgba(0, 0, 0, ${overlayOpacity / 100})`
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30 pointer-events-none"></div>
        </div>

        {/* Content */}
        <div className="relative z-20 w-full max-w-5xl mx-auto px-4 flex flex-col items-center text-center pt-16 mt-2 md:pt-16 md:mt-0 pb-4 md:pb-0">
        <h1 
          className={`text-3xl md:text-5xl font-extrabold mb-3 md:mb-5 leading-tight text-center max-w-4xl tracking-tight transition-colors duration-300 animate-reveal-up stagger-1 ${
            textColor === 'dark' ? 'text-slate-900 drop-shadow-md' : 'text-white [text-shadow:_0_2px_15px_rgb(0_0_0_/_40%)]'
          }`}
        >
          {displayTitle}
        </h1>
        <p 
          className={`text-sm md:text-lg mb-5 md:mb-10 max-w-2xl text-center font-medium leading-relaxed transition-colors duration-300 animate-reveal-up stagger-2 ${
            textColor === 'dark' ? 'text-slate-700 drop-shadow-sm' : 'text-white/95 [text-shadow:_0_2px_10px_rgb(0_0_0_/_40%)]'
          }`}
        >
          {displayDesc}
        </p>

        {/* Search Bar Container */}
        <div className="bg-white rounded-[2rem] md:rounded-full p-3 md:p-1.5 w-full max-w-4xl flex flex-col md:flex-row items-stretch md:items-center shadow-[0_12px_40px_rgb(0,0,0,0.15)] mt-2 md:mt-0 md:h-[68px] ring-1 ring-black/5 relative z-30 animate-reveal-up stagger-3">
          {/* Input Principal */}
          <div className="flex items-center flex-1 w-full px-4 md:px-5 py-3 md:py-0 h-full border-b md:border-b-0 md:border-r border-gray-100">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--brand-primary)] mr-3 shrink-0">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              value={searchLocation}
              onChange={e => setSearchLocation(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Buscar por cidade, bairro ou código..."
              className="w-full bg-transparent border-none focus:outline-none text-gray-800 placeholder:text-gray-400 text-sm md:text-base font-medium truncate"
            />
          </div>

          {/* Filtros em Grid (Mobile) / Flex (Desktop) */}
          <div className="grid grid-cols-2 md:flex md:flex-1 w-full h-full pt-4 md:pt-0">
            {/* Tipo de negócio */}
            <div className="flex items-center px-4 md:px-5 justify-between border-r border-b md:border-b-0 border-gray-100 hover:bg-gray-50/50 transition-colors py-3 md:py-0 h-full md:flex-1 group">
              <div className="flex flex-col text-left w-full">
                <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-0.5 md:mb-0">Negócio</span>
                <Select value={searchStatus} onValueChange={setSearchStatus}>
                  <SelectTrigger className="border-0 bg-transparent shadow-none focus:ring-0 p-0 h-7 md:h-5 text-gray-800 font-semibold text-sm w-full hover:bg-transparent data-[state=open]:text-[var(--brand-primary)] transition-colors">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="sale">Comprar</SelectItem>
                    <SelectItem value="rent">Alugar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tipo de imóvel */}
            <div className="flex items-center px-4 md:px-5 justify-between border-b md:border-b-0 md:border-r border-gray-100 hover:bg-gray-50/50 transition-colors py-3 md:py-0 h-full md:flex-1 group">
              <div className="flex flex-col text-left w-full">
                <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-0.5 md:mb-0">Tipo</span>
                <Select value={searchType} onValueChange={setSearchType}>
                  <SelectTrigger className="border-0 bg-transparent shadow-none focus:ring-0 p-0 h-7 md:h-5 text-gray-800 font-semibold text-sm w-full hover:bg-transparent data-[state=open]:text-[var(--brand-primary)] transition-colors">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="house">Casa</SelectItem>
                    <SelectItem value="apartment">Apto</SelectItem>
                    <SelectItem value="land">Terreno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Faixa de preço */}
            <div className="col-span-2 md:col-span-1 flex items-center px-4 md:px-5 justify-between hover:bg-gray-50/50 transition-colors py-3 md:py-0 h-full md:flex-1 group">
              <div className="flex flex-col text-left w-full">
                <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-0.5 md:mb-0">Preço Máx.</span>
                <Select value={searchPriceMax} onValueChange={setSearchPriceMax}>
                  <SelectTrigger className="border-0 bg-transparent shadow-none focus:ring-0 p-0 h-7 md:h-5 text-gray-800 font-semibold text-sm w-full hover:bg-transparent data-[state=open]:text-[var(--brand-primary)] transition-colors">
                    <SelectValue placeholder="Qualquer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Qualquer</SelectItem>
                    <SelectItem value="150000">Até R$ 150 mil</SelectItem>
                    <SelectItem value="300000">Até R$ 300 mil</SelectItem>
                    <SelectItem value="500000">Até R$ 500 mil</SelectItem>
                    <SelectItem value="1000000">Até R$ 1 milhão</SelectItem>
                    <SelectItem value="2000000">Até R$ 2 milhões</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Botão de Busca */}
          <button
            onClick={handleSearch}
            className="w-full md:w-auto h-[52px] md:h-full bg-[var(--brand-primary)] hover:opacity-90 text-white font-bold px-8 rounded-[1.25rem] md:rounded-full transition-all flex items-center justify-center mt-3 md:mt-0 md:ml-1.5 shadow-md hover:shadow-lg shrink-0 text-base active:scale-95"
          >
            Buscar
          </button>
        </div>
      </div>
      </div>
    </section>
  );
};
